import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import TriangleDiagram from "../../../../components/diagrams/TriangleDiagram";
import FractionInput from "../../../../components/inputs/FractionInput";
import RootFractionInput from "../../../../components/inputs/RootFractionInput";

// --- pomocnicze ---
function simplifySqrt(n) {
  let outside = 1;
  let inside = n;
  for (let i = 2; i * i <= inside; i++) {
    while (inside % (i * i) === 0) {
      inside /= i * i;
      outside *= i;
    }
  }
  return { outside, inside };
}

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a || 1;
}

function formatFraction(num, den) {
  const d = gcd(num, den);
  const simpNum = num / d;
  const simpDen = den / d;
  return simpDen === 1 ? `${simpNum}` : `\\dfrac{${simpNum}}{${simpDen}}`;
}

// --- główne API ---
export function generateTask(options = {}) {
  const { withAngles = false } = options;

  // Losujemy całkowite przyprostokątne
  let a, b;
  do {
    a = Math.floor(Math.random() * 8) + 2; // 2..9
    b = Math.floor(Math.random() * 8) + 2; // 2..9
  } while (a === b); // unikamy tan = 1

  const vertex = Math.random() < 0.5 ? "B" : "C";

  // Dla kąta przy B: tan = b/a
  // Dla kąta przy C: tan = a/b
  let tanNumRaw, tanDenRaw, tanValue;
  if (vertex === "B") {
    tanNumRaw = b;
    tanDenRaw = a;
    tanValue = b / a;
  } else {
    tanNumRaw = a;
    tanDenRaw = b;
    tanValue = a / b;
  }

  const d = gcd(tanNumRaw, tanDenRaw);
  const tanNum = tanNumRaw / d;
  const tanDen = tanDenRaw / d;

  // W treści: jedna przyprostokątna jest dana, druga + c do policzenia
  const knownLabel = Math.random() < 0.5 ? "a" : "b";
  const knownValue = knownLabel === "a" ? a : b;

  const hypotenuseSquared = a * a + b * b;
  const simplified = simplifySqrt(hypotenuseSquared);

  let alphaDeg = null;
  let betaDeg = null;

  if (withAngles) {
    const alphaRad = Math.atan(tanValue);
    const alpha = (alphaRad * 180) / Math.PI;
    alphaDeg = Math.round(alpha);
    betaDeg = 90 - alphaDeg;
  }

  return {
    type: "FindTriangleFromTanTask",
    data: {
      a,
      b,
      vertex,
      tanLatex: formatFraction(tanNum, tanDen),
      tanValue,
      hypotenuseSquared,
      simplified,
      knownLabel,
      knownValue,
      withAngles,
      alphaDeg,
      betaDeg,
    },
    expectedAnswer: { a, b },
  };
}

export function renderTask({ data }) {
  const { a, b, vertex, tanLatex, knownLabel, knownValue, withAngles } = data;

  const labelSides = {
    a: knownLabel === "a" ? `${knownValue}` : "a",
    b: knownLabel === "b" ? `${knownValue}` : "b",
    c: "c",
  };

  return (
    <>
      <p>
        Na podstawie rysunku poniżej, oblicz długość drugiej przyprostokątnej
        oraz przeciwprostokątnej. Kąt{" "}
        <InlineMath math={'\\alpha'} /> znajduje się w wierzchołku{" "}
        <InlineMath math={vertex} />, a{" "}
        <InlineMath math={`\\tan\\alpha = ${tanLatex}`} />.
      </p>
      {withAngles && (
        <p>
          Dodatkowo podaj miary kątów{" "}
          <InlineMath math={'\\alpha'} /> (zaznaczonym na rysunku) i{" "}
          <InlineMath math={'\\beta'} /> (drugim kątem ostrym tego trójkąta
          prostokątnego) w stopniach, z dokładnością do{" "}
          <InlineMath math={'1^\\circ'} />.
        </p>
      )}
      <TriangleDiagram
        a={a}
        b={b}
        angleAt={vertex}
        labelSides={labelSides}
      />
    </>
  );
}


export function renderInput({ data }, userAnswer, onChange, disabled) {
  const ans = userAnswer || {};
  const needA = data.knownLabel !== "a";
  const needB = data.knownLabel !== "b";

  return (
    <div className="flex flex-col gap-4">
      {/* tylko ten bok, który NIE jest podany w treści */}
      {needA && (
        <div className="flex items-center gap-2">
          <InlineMath math="a =" />
          <FractionInput
            value={ans.a || ""}
            onChange={(val) => onChange({ ...ans, a: val })}
            disabled={disabled}
            label="a"
          />
        </div>
      )}
      {needB && (
        <div className="flex items-center gap-2">
          <InlineMath math="b =" />
          <FractionInput
            value={ans.b || ""}
            onChange={(val) => onChange({ ...ans, b: val })}
            disabled={disabled}
            label="b"
          />
        </div>
      )}

      {/* przeciwprostokątna z pierwiastkiem */}
      <div className="flex items-center gap-2">
        <InlineMath math="c =" />
        <RootFractionInput
          value={ans.c || ""}
          onChange={(val) => onChange({ ...ans, c: val })}
          disabled={disabled}
          label="c"
        />
      </div>

      {/* kąty – stopnie dopisywane jako jednostka za liczbą */}
      {data.withAngles && (
        <>
          <div className="flex items-center gap-2">
            <InlineMath math={"\\alpha ="} />
            <FractionInput
              value={ans.alpha || ""}
              onChange={(val) => onChange({ ...ans, alpha: val })}
              disabled={disabled}
              label="\alpha"
              suffixLatex="^\circ"
            />
          </div>
          <div className="flex items-center gap-2">
            <InlineMath math={"\\beta ="} />
            <FractionInput
              value={ans.beta || ""}
              onChange={(val) => onChange({ ...ans, beta: val })}
              disabled={disabled}
              label="\beta"
              suffixLatex="^\circ"
            />
          </div>
        </>
      )}

    </div>
  );
}


export function checkAnswer({ data }, userAnswer) {
  const needA = data.knownLabel !== "a";
  const needB = data.knownLabel !== "b";

  // --- WERSJA BEZ KĄTÓW: dalej prosty boolean (1 pkt) ---
  if (!data.withAngles) {
    if (!userAnswer || typeof userAnswer !== "object") return false;

    const parsedA = needA ? userAnswer.a?.parsed : data.a;
    const parsedB = needB ? userAnswer.b?.parsed : data.b;
    const parsedC = userAnswer.c?.parsed;

    if (parsedC == null) return false;
    if (needA && parsedA == null) return false;
    if (needB && parsedB == null) return false;

    const okA = !needA || parsedA === data.a;
    const okB = !needB || parsedB === data.b;
    const cExact = Math.sqrt(data.hypotenuseSquared);
    const okC = Math.abs(parsedC - cExact) < 1e-6;

    return okA && okB && okC;
  }

  // --- WERSJA Z KĄTAMI: zawsze obiekt z maxScore = 2 ---
  if (!userAnswer || typeof userAnswer !== "object") {
    return {
      isCorrect: false,
      score: 0,
      maxScore: 2,
      parts: { boki: false, "kąty": false },
    };
  }

  const parsedA = needA ? userAnswer.a?.parsed ?? null : data.a;
  const parsedB = needB ? userAnswer.b?.parsed ?? null : data.b;
  const parsedC = userAnswer.c?.parsed ?? null;

  const cExact = Math.sqrt(data.hypotenuseSquared);

  const okA = !needA || (parsedA !== null && parsedA === data.a);
  const okB = !needB || (parsedB !== null && parsedB === data.b);
  const okC = parsedC !== null && Math.abs(parsedC - cExact) < 1e-6;
  const okSides = okA && okB && okC;

  const alphaParsed = userAnswer.alpha?.parsed ?? null;
  const betaParsed = userAnswer.beta?.parsed ?? null;

  const alphaOk =
    alphaParsed !== null && Math.round(alphaParsed) === data.alphaDeg;
  const betaOk =
    betaParsed !== null && Math.round(betaParsed) === data.betaDeg;
  const okAngles = alphaOk && betaOk;

  let score = 0;
  if (okSides) score += 1;   // komplet wszystkich wymaganych boków
  if (okAngles) score += 1;  // oba kąty poprawne

  return {
    isCorrect: score === 2,
    score,
    maxScore: 2,
    parts: {
      boki: okSides,
      "kąty": okAngles,
    },
  };
}



export function renderAnswer({ data }) {
  const { a, b, simplified, knownLabel, withAngles, alphaDeg, betaDeg } = data;

  const missingLabel = knownLabel === "a" ? "b" : "a";
  const missingValue = knownLabel === "a" ? b : a;

  const hypLatex =
    simplified.outside === 1
      ? `\\sqrt{${simplified.inside}}`
      : `${simplified.outside}\\sqrt{${simplified.inside}}`;

  return (
    <div className="space-y-2 mt-2">
      <div>
        <InlineMath math={`${missingLabel} = ${missingValue}`} />
      </div>
      <div>
        <InlineMath math={`c = ${hypLatex}`} />
      </div>
      {withAngles && (
        <>
          <div>
            <InlineMath math={`\\alpha \\approx ${alphaDeg}^\\circ`} />
          </div>
          <div>
            <InlineMath math={`\\beta \\approx ${betaDeg}^\\circ`} />
          </div>
        </>
      )}
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    (!userAnswer.a?.raw && !userAnswer.b?.raw && !userAnswer.c?.raw)
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const parts = [];
  if (userAnswer.a?.raw != null) parts.push(`a = ${userAnswer.a.raw}`);
  if (userAnswer.b?.raw != null) parts.push(`b = ${userAnswer.b.raw}`);
  if (userAnswer.c?.raw != null) parts.push(`c = ${userAnswer.c.raw}`);
  if (userAnswer.alpha?.raw != null)
    parts.push(`\\alpha = ${userAnswer.alpha.raw}^\\circ`);
  if (userAnswer.beta?.raw != null)
    parts.push(`\\beta = ${userAnswer.beta.raw}^\\circ`);

  return <InlineMath math={parts.join(",\\ ")} />;
}

export function renderExplanation({ data }) {
  const {
    a,
    b,
    tanLatex,
    knownLabel,
    hypotenuseSquared,
    simplified,
    withAngles,
    alphaDeg,
    betaDeg,
  } = data;

  const missingLabel = knownLabel === "a" ? "b" : "a";
  const missingValue = knownLabel === "a" ? b : a;

  const hypLatex =
    simplified.outside === 1
      ? `\\sqrt{${simplified.inside}}`
      : `${simplified.outside}\\sqrt{${simplified.inside}}`;

  return (
    <div className="space-y-4">
      <div>
        <strong>Krok 1:</strong> Korzystamy z definicji tangensa:
        <BlockMath math={'\\tan\\alpha = \\dfrac{\\text{przyprostokątna naprzeciw }\\alpha}{\\text{przyprostokątna przyległa do }\\alpha}'} />
      </div>

      <div>
        <strong>Krok 2:</strong> Podstawiamy dane:
        <BlockMath
          math={`\\tan\\alpha = ${tanLatex} = \\dfrac{${missingLabel}}{${knownLabel}}`}
        />
      </div>

      <div>
        <strong>Krok 3:</strong> Mnożymy obustronnie przez{" "}
        <InlineMath math={knownLabel} /> i wyznaczamy drugą przyprostokątną:
        <BlockMath
          math={`${tanLatex} = \\dfrac{${missingLabel}}{${knownLabel}}`}
        />
        <BlockMath
          math={`${missingLabel} = ${tanLatex} \\cdot ${knownLabel}`}
        />
        <BlockMath math={`${missingLabel} = ${missingValue}`} />
      </div>

      <div>
        <strong>Krok 4:</strong> Liczymy przeciwprostokątną z twierdzenia Pitagorasa:
        <BlockMath math={'a^2 + b^2 = c^2'} />
        <BlockMath math={`c^2 = ${a}^2 + ${b}^2 = ${hypotenuseSquared}`} />
        <BlockMath math={`c = \\sqrt{${hypotenuseSquared}} = ${hypLatex}`} />
      </div>

      {withAngles && (
        <div>
          <strong>Krok 5:</strong> Liczymy kąty.
          <BlockMath math={`\\tan\\alpha = ${tanLatex}`} />
          <BlockMath math={`\\alpha \\approx ${alphaDeg}^\\circ`} />
          <BlockMath math={`\\beta = 90^\\circ - \\alpha \\approx ${betaDeg}^\\circ`} />
        </div>
      )}

      <div>
        <strong>Odpowiedź końcowa:</strong>
        <BlockMath
          math={
            withAngles
              ? `${missingLabel} = ${missingValue},\\ c = ${hypLatex},\\ \\alpha \\approx ${alphaDeg}^\\circ,\\ \\beta \\approx ${betaDeg}^\\circ`
              : `${missingLabel} = ${missingValue},\\ c = ${hypLatex}`
          }
        />
      </div>
    </div>
  );
}
